import React from 'react';
import { connect } from 'react-redux';
import { injectIntl, IntlProvider } from 'react-intl';
import { Row, Col, Button, OverlayTrigger, Popover, Badge } from 'react-bootstrap';
import { SketchPicker } from 'react-color';
import { findIndex as _findIndex } from 'lodash';

import { getTextColor } from 'components/forms/autocomplete/Color';

import EntityPropsUpdater from 'component/EntityPropsUpdater';
import IntlFormInlineSelector from 'components/intlForm/IntlFormInlineSelector';
import Entity from 'components/Entity';

import FormInput from 'components/forms/FormInput';
import FormCodeEditor from 'components/forms/FormCodeEditor';

import CmsCanvas from 'components/cms/CmsCanvas';

import { entityMetaMapping } from 'js/constants';
import EmailPreview from 'components/modals/EmailPreview';

class WhiteLabelStyle extends EntityPropsUpdater {
  constructor(props) {
    super(props);

    this.state = {
      showEmailPreview: false,
    };
  }

  // make sure there is a CMS footer for given language
  getFooterEntity = () => {
    const { entity, appIntl } = this.props;

    const footerIndex = _findIndex(entity.getFooter(), ['language', appIntl.displayIntlForm]);
    if (footerIndex > -1) {
      return this.getEntityForCms(entity.getFooter()[footerIndex]);
    }

    // create new value and return it
    const newValue = entity.getFooter().slice();
    const newFooter = entity.createValue('footer', [], { language: appIntl.displayIntlForm });
    newValue.push(newFooter);

    this.updateEntity('footer', newValue);

    return this.getEntityForCms(newFooter);
  };

  getEntityForCms = data => {
    const entity = new Entity();
    entity.setData(data);
    entity.setMeta(this.props.meta, entityMetaMapping.cmsDescription);

    return entity;
  };

  updateFooter = content => {
    const { entity, appIntl } = this.props;
    const footerIndex = _findIndex(entity.getFooter(), ['language', appIntl.displayIntlForm]);
    this.updateEntity('content', content, ['footer', footerIndex.toString()]);
  };

  removeThemeColor = index => {
    const { entity } = this.props;
    const themeColors = entity.getThemeColors().slice();
    themeColors.splice(index, 1);
    this.updateEntity('themeColors', themeColors);
  };

  addThemeColor = () => {
    const { entity } = this.props;
    const themeColors = entity.getThemeColors().slice();
    let index = themeColors.length;
    do {
      index++;
    } while (_findIndex(themeColors, ['name', 'Color ' + index]) > -1);
    themeColors.push(entity.createValue('themeColors', [], { name: 'Color ' + index, color: '#ffffff' }));
    this.updateEntity('themeColors', themeColors);
  };

  editThemeColor = (index, propertyName, propertyValue) => {
    this.updateEntity(propertyName, propertyValue, ['themeColors', index.toString()]);
  };

  showEmailPreviewF = () => {
    this.setState({ showEmailPreview: true });
  };

  closeEmailPreviewF = () => {
    this.setState({ showEmailPreview: false });
  };

  render() {
    const {
      intl: { messages },
      intl,
      entity,
      entityLoaded,
      disabled,
      inModal,
    } = this.props;

    const { showEmailPreview } = this.state;

    return (
      <div className="clearfix">
        {entityLoaded ? (
          <div>
            <Row>
              <Col xs={12} md={inModal ? 12 : 4}>
                <div className="panel panel-transparent">
                  <div className="panel-body">
                    <p className="form-group-title">{messages.html_header}</p>
                    <div className="form-group-attached">
                      <FormCodeEditor
                        label={messages.custom_html_header}
                        name="customHeader"
                        value={entity.getCustomHeader()}
                        meta={entity.getCustomHeaderMetaInformation()}
                        validation={entity.getCustomHeaderValidationResult()}
                        onChange={this.updateEntity}
                        disabled={disabled}
                        language="html"
                      />
                    </div>

                    <p className="m-t-20 form-group-title">{messages.theme_colors}</p>
                    <div className="form-group-attached">
                      {entity.getThemeColors().map((themeColor, i) => {
                        const themeColorBadge = (
                          <Badge
                            style={{
                              background: themeColor.getColor(),
                              color: getTextColor(themeColor.getColor()),
                            }}
                            className="p-l-10 p-r-10 p-t-5 p-b-5 lh-15"
                          >
                            {themeColor.getColor()}
                          </Badge>
                        );

                        return (
                          <Row className="clearfix" key={themeColor.getColor()}>
                            <Col xs={5}>
                              <FormInput
                                label={messages.name}
                                name="name"
                                value={themeColor.getName()}
                                meta={themeColor.getNameMetaInformation()}
                                validation={themeColor.getNameValidationResult()}
                                onChange={(name, value) => this.editThemeColor(i, name, value)}
                                disabled={disabled}
                              />
                            </Col>
                            <Col xs={5}>
                              <div className="form-group form-group-default" style={{ position: 'relative' }}>
                                <label>{messages.color}</label>
                                <div style={{ position: 'relative', width: '100%' }}>
                                  {disabled ? (
                                    themeColorBadge
                                  ) : (
                                    <OverlayTrigger
                                      trigger="click"
                                      rootClose
                                      placement="top"
                                      overlay={
                                        <Popover className="popover-modal">
                                          <IntlProvider locale={intl.locale} messages={intl.messages}>
                                            <SketchPicker
                                              disableAlpha
                                              onChange={({ hex }) => this.editThemeColor(i, 'color', hex)}
                                              color={themeColor.getColor()}
                                            />
                                          </IntlProvider>
                                        </Popover>
                                      }
                                    >
                                      {themeColorBadge}
                                    </OverlayTrigger>
                                  )}
                                </div>
                              </div>
                            </Col>
                            <Col xs={2}>
                              <div className="form-group form-group-default" style={{ position: 'relative' }}>
                                <label>&nbsp;</label>
                                <Button
                                  bsStyle="danger"
                                  block
                                  bsSize="xs"
                                  onClick={() => this.removeThemeColor(i)}
                                  disabled={disabled}
                                >
                                  <i className="fal fa-times" />
                                </Button>
                              </div>
                            </Col>
                          </Row>
                        );
                      })}
                    </div>

                    <div className="text-center p-t-15">
                      <Button className="btn-rounded" onClick={this.addThemeColor} disabled={disabled}>
                        <i className="fa fa-plus" />
                      </Button>
                    </div>
                  </div>
                </div>
              </Col>

              <Col xs={12} md={inModal ? 12 : 4}>
                <div className="panel panel-transparent">
                  <div className="panel-body">
                    <p className="form-group-title">{messages.emails}</p>
                    <div>
                      <Button
                        onClick={() => {
                          this.showEmailPreviewF();
                        }}
                        disabled={disabled}
                      >
                        {messages.mail_preview}
                      </Button>
                      <EmailPreview
                        alreadyInModal={inModal}
                        show={showEmailPreview}
                        close={() => {
                          this.closeEmailPreviewF();
                        }}
                      />
                    </div>
                    <div className="form-group-attached">
                      <FormCodeEditor
                        label={messages.custom_css_mail}
                        name="customCssMail"
                        value={entity.getCustomCssMail()}
                        meta={entity.getCustomCssMailMetaInformation()}
                        validation={entity.getCustomCssMailValidationResult()}
                        onChange={this.updateEntity}
                        disabled={disabled}
                        language="css"
                      />
                    </div>
                  </div>
                </div>
              </Col>

              <Col xs={12} md={inModal ? 12 : 4}>
                <div className="panel panel-transparent">
                  <div className="panel-body">
                    <p className="form-group-title">{messages.main_menu}</p>
                    <div className="form-group-attached">
                      <FormInput
                        label={messages.menu_css_class}
                        name="menuClass"
                        value={entity.getMenuClass()}
                        meta={entity.getMenuClassMetaInformation()}
                        validation={entity.getMenuClassValidationResult()}
                        onChange={this.updateEntity}
                        disabled={disabled}
                      />
                      <FormInput
                        label={messages.menu_css_class_scroll}
                        name="menuScrollClass"
                        value={entity.getMenuScrollClass()}
                        meta={entity.getMenuScrollClassMetaInformation()}
                        validation={entity.getMenuScrollClassValidationResult()}
                        onChange={this.updateEntity}
                        disabled={disabled}
                      />
                    </div>
                  </div>
                </div>
              </Col>
            </Row>

            <Row>
              <Col xs={12}>
                <div className="p-t-15 p-b-15" style={{ textAlign: 'center' }}>
                  <IntlFormInlineSelector /> {messages.footer}
                </div>
                <CmsCanvas
                  editMode={!disabled}
                  entity={this.getFooterEntity()}
                  changeEntity={footerEntity => {
                    this.updateFooter(footerEntity.processedData.content);
                  }}
                />
              </Col>
            </Row>
          </div>
        ) : null}
      </div>
    );
  }
}
WhiteLabelStyle.defaultProps = {
  inModal: false,
};

const mapStateToProps = state => {
  return {
    appIntl: state.intl,
    meta: state.entityMeta,
  };
};

export default injectIntl(connect(mapStateToProps)(WhiteLabelStyle));
